import { type EventState } from '@solinkcorp/api-types';
import React from 'react';
import useSWR from 'swr';

interface AuditLogAddEntry {
  type: 'add';
  answer: string;
  updatedBy: any;
  updatedTime: number;
}

interface AuditLogRemoveEntry {
  type: 'remove';
  updatedBy: any;
  updatedTime: number;
}

type AuditLogEntry = AuditLogAddEntry | AuditLogRemoveEntry;

export type AuditLogList = AuditLogEntry[];

export default function useAuditLog(getAuditLog: () => Promise<EventState[] | undefined>) {
  const [isOpen, setIsOpen] = React.useState(false);

  // Only fetch the audit log if the log is open
  const { data, error, isLoading } = useSWR(isOpen ? 'auditLog' : null, async () => {
    const auditLog = await getAuditLog();

    if (!auditLog) {
      return;
    }

    const processedAuditLog: AuditLogList = [];

    for (const log of auditLog) {
      if (
        log.state === 'CLOSED' ||
        (log.state === 'OPEN' && log.data?.type === 'verification-claim' && log.data?.resetBy)
      ) {
        if (log.data?.type === 'verification-claim') {
          processedAuditLog.push({
            type: 'remove',
            updatedBy: log.data.resetBy,
            updatedTime: log.data.resetAt,
          } as AuditLogRemoveEntry);
        }

        if (log.data?.type === 'verification-prompt') {
          processedAuditLog.push({
            type: 'add',
            answer: log.data?.transition,
            updatedBy: log.data?.updatedBy,
            updatedTime: log.data?.updatedTime,
          } as AuditLogAddEntry);
        }
      }
    }

    return processedAuditLog;
  });

  const toggleAuditLog = React.useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return {
    auditLog: data,
    isLoading,
    error,
    isOpen,
    toggleAuditLog,
  };
}
